import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProcessTemplateTaskType } from '@econtractor/types';
import { PROCESS_TEMPLATE_NAME_MAX_LENGTH } from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import { createProcessTemplate, getProcessTemplate, updateProcessTemplate } from 'api/apiTask';

interface UseOffProcessDirParams {
    show: boolean;
    templateId: number | null;
    onHide: () => void;
    onSaved: () => void;
}

export const useOffProcessDir = ({ show, templateId, onHide, onSaved }: UseOffProcessDirParams) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const [tasks, setTasks] = useState<ProcessTemplateTaskType[]>([]);
    const [showDescription, setShowDescription] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedId, setSavedId] = useState<number | null>(templateId);
    const [sessionShow, setSessionShow] = useState(show);
    const [sessionId, setSessionId] = useState(templateId);

    if (show !== sessionShow || templateId !== sessionId) {
        setSessionShow(show);
        setSessionId(templateId);
        setSavedId(templateId);
        setName('');
        setDescription('');
        setCategoryId(null);
        setTasks([]);
        setShowDescription(false);
        setActiveTab('general');
        setSaving(false);
        setLoading(Boolean(show && templateId));
    }

    useEffect(() => {
        if (!show || !templateId) return;

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            const card = await getProcessTemplate(templateId);
            if (cancelled) return;
            if (!card) {
                showToast(t('common:messError'), 'danger');
                setLoading(false);
                onHide();
                return;
            }
            setName(card.name || '');
            setDescription(card.description || '');
            setCategoryId(card.categoryId);
            setTasks(card.tasks || []);
            setShowDescription(Boolean(String(card.description || '').replace(/<[^>]*>/g, '').trim()));
            setLoading(false);
        };

        void load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, templateId]);

    const reportSaveError = (errCode?: string) => {
        if (errCode === 'EMPTY_NAME') showToast(t('processDir.errEmptyName'), 'danger');
        else if (errCode === 'NAME_TOO_LONG') showToast(t('processDir.errNameTooLong'), 'danger');
        else if (errCode === 'INVALID_TASKS') showToast(t('processDir.errTasks'), 'danger');
        else showToast(t('common:messError'), 'danger');
    };

    const persist = async (nextTasks: ProcessTemplateTaskType[], close: boolean) => {
        const trimmed = name.trim();
        if (!trimmed) {
            showToast(t('processDir.errEmptyName'), 'danger');
            setActiveTab('general');
            return false;
        }
        if (trimmed.length > PROCESS_TEMPLATE_NAME_MAX_LENGTH) {
            showToast(t('processDir.errNameTooLong'), 'danger');
            setActiveTab('general');
            return false;
        }

        setSaving(true);
        try {
            const payload = {
                name: trimmed,
                description,
                categoryId,
                tasks: nextTasks,
            };
            const result = savedId
                ? await updateProcessTemplate(savedId, payload)
                : await createProcessTemplate(payload);
            if (!result?.stat) {
                reportSaveError(result?.errCode);
                return false;
            }
            const nextId = result.templateId || savedId;
            if (nextId) setSavedId(nextId);
            onSaved();
            if (close) {
                showToast(t('processDir.messSaved'), 'success');
                onHide();
            }
            return true;
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => persist(tasks, true);

    const persistTasks = (nextTasks: ProcessTemplateTaskType[]) => persist(nextTasks, false);

    return {
        id: savedId,
        name,
        setName,
        description,
        setDescription,
        categoryId,
        setCategoryId,
        tasks,
        setTasks,
        showDescription,
        setShowDescription,
        activeTab,
        setActiveTab,
        loading,
        saving,
        handleSave,
        persistTasks,
    };
};
